import { IMilestone, IMilestoneBase } from "@/interfaces";

/***
 * findTargetParentMilestoneRecursive will return the milestone with the specified Id
 * @param milestones: is list of ProjectMilestones or ProjectMilestoneTemplates
 * @param targetId: is either the parentMilestone Id or the MilestoneTemplateId (to match the Template)
 */
export const findTargetMilestoneRecursive = (
  milestones: IMilestoneBase[] | IMilestone[],
  targetId: string
): IMilestone | IMilestoneBase | undefined => {
  let foundParent = milestones.find(({ id }) => id === targetId);

  if (foundParent) return foundParent;

  for (const milestone of milestones) {
    if (!milestone.childMilestones?.length) continue;

    foundParent = findTargetMilestoneRecursive(
      milestone.childMilestones,
      targetId
    );

    if (foundParent) break;
  }

  return foundParent;
};

export const updateMilestone = (
  milestones: IMilestone[],
  updatedMilestone: IMilestone
): IMilestone[] =>
  milestones.map((milestone) => {
    if (milestone.id === updatedMilestone.id) {
      return { ...milestone, ...updatedMilestone };
    }
    let childMilestones = milestone.childMilestones;
    if (milestone.childMilestones) {
      childMilestones = updateMilestone(
        milestone.childMilestones,
        updatedMilestone
      );
    }
    return {
      ...milestone,
      childMilestones: childMilestones,
    };
  });

export const deleteMilestone = (
  milestones: IMilestone[],
  milestoneId: string
): IMilestone[] => {
  const results = milestones
    ?.filter(({ id }) => id !== milestoneId)
    .map((milestone) => {
      const { childMilestones } = milestone;

      if (!childMilestones?.length) return { ...milestone };

      const updatedMilestone = {
        ...milestone,
        childMilestones: deleteMilestone(childMilestones, milestoneId),
      };

      if (!updatedMilestone.childMilestones?.length) {
        updatedMilestone.childMilestones = [];
      }

      return updatedMilestone;
    });

  return results?.filter((result) => !!result);
};

export const getMilestoneUsers = (milestones: IMilestone[]): string[] => {
  let users: string[] = [];

  milestones?.forEach(({ approvers, childMilestones }) => {
    if (approvers?.length) {
      users = [...users, ...approvers.map(({ userId }) => userId)];
    }

    if (childMilestones?.length) {
      users = [...users, ...getMilestoneUsers(childMilestones)];
    }
  });

  return users;
};

export const getMilestoneNotificationAdGroupIds = (
  milestones?: IMilestone[]
): string[] => {
  let adGroupIds: string[] = [];

  if (!milestones) return adGroupIds;

  milestones.forEach(({ notification: { groups } = {}, childMilestones }) => {
    adGroupIds = [
      ...adGroupIds,
      ...(
        groups?.map(({ azureActiveDirectoryId }) => azureActiveDirectoryId) ||
        []
      ).filter((group) => !!group),
    ];
    adGroupIds = [
      ...adGroupIds,
      ...getMilestoneNotificationAdGroupIds(childMilestones),
    ];
  });

  return adGroupIds;
};
